import { message } from 'antd';
import * as Sentry from '@sentry/react';
// html2canvas and jsPDF both ship as dependencies of html2pdf.js (already in the lockfile).
// We drive them directly instead of using html2pdf's one-shot pipeline because we need to
// decide the page break positions ourselves: html2pdf slices the captured canvas at fixed
// page heights with no awareness of where a chart or table card begins and ends, which is what
// cut the charts in half across pages. Here we capture the page once and then place the slices
// so that no card that fits within a page is ever split across two pages.
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// A4 landscape, millimetres. Margins are applied on every side and the captured image is placed
// at the full content width, so vertical canvas pixels map to millimetres by the same ratio.
const PAGE_WIDTH_MM = 297;
const PAGE_HEIGHT_MM = 210;
const PAGE_MARGIN_MM = 10;
const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - PAGE_MARGIN_MM * 2;
const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - PAGE_MARGIN_MM * 2;

const CAPTURE_SCALE = 2;
const IMAGE_FORMAT = 'JPEG';
const IMAGE_MIME = 'image/jpeg';
const IMAGE_QUALITY = 0.95;

// When a page boundary lands inside a card we pull the cut up to the card's top so the whole
// card moves to the next page. Skip that pull-back if it would leave the current page less than
// this fraction filled, so a card that is itself ~one page tall doesn't leave a near-blank page.
const MIN_PAGE_FILL_RATIO = 0.12;
const EPSILON_PX = 1;
// Drop a final trailing slice this small (source-canvas px) rather than emit a near-blank page
// for it. Kept tiny so it can only ever be capture rounding / whitespace, never real content —
// even a single table row is far taller than this once scaled by CAPTURE_SCALE.
const MIN_TRAILING_SLICE_PX = 4;

type CardBoundsCss = { topCss: number; bottomCss: number };
type CardBoundsPx = { top: number; bottom: number };

/**
 * Exports the analytics page identified by `rootSelector` as a landscape A4 PDF, keeping each
 * chart/table card whole instead of letting it be sliced across a page boundary.
 *
 * Why we capture the whole page in one pass (rather than each card individually): the analytics
 * pages also render content that lives outside any card — the KPI page's facility-name header,
 * the selected-filter pills, multi-column card rows — and a per-card capture would drop or
 * reflow those. Capturing the root preserves the exact on-screen layout; we only change where
 * the page breaks fall.
 */
export const exportAnalyticsPageAsPdf = async (
	rootSelector: string,
	filename: string
): Promise<void> => {
	const root = document.querySelector(rootSelector) as HTMLElement | null;
	if (!root) {
		message.error('Could not find page content to export.');
		return;
	}

	const hidePdfLoadingMessage = message.loading('Generating PDF…', 0);

	try {
		// Measure every top-level card's vertical span BEFORE capture. Measurements come from the
		// live DOM; the capture preserves the same layout — chart <canvas> elements are swapped for
		// equal-height <img> elements and hidden controls keep their box via visibility:hidden — so
		// these bounds map 1:1 onto the rows of the captured canvas.
		const cardBoundsCss = getTopLevelCardBounds(root);

		const canvas = await html2canvas(root, {
			scale: CAPTURE_SCALE,
			useCORS: true,
			logging: false,
			backgroundColor: '#ffffff',
			onclone: (clonedDoc: Document) => {
				replaceChartCanvasesWithImages(clonedDoc, rootSelector);
				hideNonPrintableElements(clonedDoc, rootSelector);
				showPdfOnlyElements(clonedDoc, rootSelector);
			},
		});

		const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
		renderCanvasAcrossPages(pdf, canvas, cardBoundsCss);
		pdf.save(filename);
	} catch (error) {
		Sentry.captureException(error);
		message.error('Could not generate the PDF. Please try again.');
	} finally {
		hidePdfLoadingMessage();
	}
};

/**
 * Vertical bounds (relative to the root's top, in CSS pixels) of each outermost `.ant-card`.
 * Cards nested inside another card are dropped — the ancestor already protects them, and adding
 * both would create redundant break points.
 */
const getTopLevelCardBounds = (root: HTMLElement): CardBoundsCss[] => {
	const rootTop = root.getBoundingClientRect().top;
	const allCards = Array.from(root.querySelectorAll('.ant-card')) as HTMLElement[];
	const topLevelCards = allCards.filter(
		(card) => !allCards.some((other) => other !== card && other.contains(card))
	);
	return topLevelCards.map((card) => {
		const rect = card.getBoundingClientRect();
		return { topCss: rect.top - rootTop, bottomCss: rect.bottom - rootTop };
	});
};

/**
 * BizCharts/G2 render charts into <canvas> elements, which html2canvas captures unreliably (it
 * ignores CSS transforms applied to canvas). Swap each cloned canvas for an <img> built from the
 * live canvas's pixels, keeping the same height so the layout — and therefore our measured card
 * bounds — stays aligned.
 */
const replaceChartCanvasesWithImages = (clonedDoc: Document, rootSelector: string): void => {
	const liveCanvases = Array.from(
		document.querySelectorAll(`${rootSelector} canvas`)
	) as HTMLCanvasElement[];
	const clonedCanvases = Array.from(
		clonedDoc.querySelectorAll(`${rootSelector} canvas`)
	) as HTMLCanvasElement[];

	clonedCanvases.forEach((clonedCanvas, index) => {
		if (index >= liveCanvases.length) {
			return;
		}
		try {
			const image = clonedDoc.createElement('img') as HTMLImageElement;
			image.src = liveCanvases[index].toDataURL('image/png');
			image.style.display = 'block';
			image.style.width = '100%';
			image.style.height =
				liveCanvases[index].style.height || liveCanvases[index].offsetHeight + 'px';
			if (clonedCanvas.parentNode) {
				clonedCanvas.parentNode.replaceChild(image, clonedCanvas);
			}
		} catch (_error) {
			// Cross-origin tainted canvas — leave the original canvas in place.
		}
	});
};

/**
 * Hide the action buttons (filter/download/print) and per-card controls in the clone. We use
 * visibility:hidden rather than display:none so the elements keep occupying their layout box;
 * removing the box would shift everything below it and break the alignment between the captured
 * canvas and the card bounds we measured from the live DOM.
 */
const hideNonPrintableElements = (clonedDoc: Document, rootSelector: string): void => {
	const selector = [
		`${rootSelector} .analytics-pdf-hide-in-print`,
		`${rootSelector} .ant-card-extra button`,
	].join(', ');
	(Array.from(clonedDoc.querySelectorAll(selector)) as HTMLElement[]).forEach((element) => {
		element.style.visibility = 'hidden';
	});
};

/**
 * Make elements marked `analytics-pdf-only` visible in the clone. These elements have
 * `display:none` in the live DOM so they don't affect the normal UI, but they should appear
 * in the exported PDF (e.g. page-title headings that the OverviewShell header normally provides
 * but which fall outside the captured root selector).
 */
const showPdfOnlyElements = (clonedDoc: Document, rootSelector: string): void => {
	(
		Array.from(
			clonedDoc.querySelectorAll(`${rootSelector} .analytics-pdf-only`)
		) as HTMLElement[]
	).forEach((element) => {
		element.style.display = 'block';
	});
};

/**
 * Slice the captured canvas into A4-landscape pages, pulling each page break up to a card's top
 * whenever the naive break would fall inside a card that fits within a single page.
 */
const renderCanvasAcrossPages = (
	pdf: jsPDF,
	canvas: HTMLCanvasElement,
	cardBoundsCss: CardBoundsCss[]
): void => {
	const canvasWidthPx = canvas.width;
	const canvasHeightPx = canvas.height;
	if (!canvasWidthPx || !canvasHeightPx) {
		return;
	}

	// html2canvas draws the content with ctx.scale(CAPTURE_SCALE, CAPTURE_SCALE), so a CSS pixel
	// maps to exactly CAPTURE_SCALE source-canvas pixels. (Reconstructing the ratio from
	// canvas.width / cssWidth would be off by a sub-pixel because html2canvas ceils the element
	// size then floors the backing-store dimensions — that error accumulates over a tall page.)
	const cardBoundsPx: CardBoundsPx[] = cardBoundsCss.map((bounds) => ({
		top: bounds.topCss * CAPTURE_SCALE,
		bottom: bounds.bottomCss * CAPTURE_SCALE,
	}));

	// One printable page expressed in source-canvas pixels.
	const pxPerMm = canvasWidthPx / CONTENT_WIDTH_MM;
	const pagePx = CONTENT_HEIGHT_MM * pxPerMm;
	const minPageFillPx = pagePx * MIN_PAGE_FILL_RATIO;

	const pageCanvas = document.createElement('canvas');
	const pageContext = pageCanvas.getContext('2d');
	pageCanvas.width = canvasWidthPx;

	let sliceTopPx = 0;
	let isFirstPage = true;

	while (sliceTopPx < canvasHeightPx - EPSILON_PX) {
		const naiveBottomPx = sliceTopPx + pagePx;
		let sliceBottomPx = Math.min(naiveBottomPx, canvasHeightPx);

		if (naiveBottomPx < canvasHeightPx) {
			const pullTopPx = findBreakAvoidingCardCut(cardBoundsPx, sliceTopPx, naiveBottomPx, pagePx);
			if (pullTopPx !== null && pullTopPx > sliceTopPx + minPageFillPx) {
				sliceBottomPx = pullTopPx;
			}
		}

		const sliceHeightPx = Math.max(1, Math.round(sliceBottomPx - sliceTopPx));

		// Skip a tiny trailing remainder so we don't emit an otherwise-blank final page.
		const isFinalSlice = sliceBottomPx >= canvasHeightPx - EPSILON_PX;
		if (isFinalSlice && !isFirstPage && sliceHeightPx <= MIN_TRAILING_SLICE_PX) {
			break;
		}

		pageCanvas.height = sliceHeightPx;
		if (pageContext) {
			pageContext.fillStyle = '#ffffff';
			pageContext.fillRect(0, 0, pageCanvas.width, sliceHeightPx);
			pageContext.drawImage(
				canvas,
				0,
				Math.round(sliceTopPx),
				canvasWidthPx,
				sliceHeightPx,
				0,
				0,
				canvasWidthPx,
				sliceHeightPx
			);
		}

		const sliceHeightMm = sliceHeightPx / pxPerMm;
		const imageData = pageCanvas.toDataURL(IMAGE_MIME, IMAGE_QUALITY);
		if (!isFirstPage) {
			pdf.addPage();
		}
		pdf.addImage(
			imageData,
			IMAGE_FORMAT,
			PAGE_MARGIN_MM,
			PAGE_MARGIN_MM,
			CONTENT_WIDTH_MM,
			sliceHeightMm
		);

		isFirstPage = false;
		sliceTopPx = sliceBottomPx;
	}
};

/**
 * If the page break at `naiveBottomPx` would cut through one or more cards that start on the
 * current page and fit within a single page, return the topmost such card's top so the break can
 * be pulled up to it. Returns null when nothing needs avoiding (clean break in a gap, or the only
 * straddling card is taller than a page and therefore cannot be kept whole regardless).
 */
const findBreakAvoidingCardCut = (
	cardBoundsPx: CardBoundsPx[],
	sliceTopPx: number,
	naiveBottomPx: number,
	pagePx: number
): number | null => {
	let pullTopPx: number | null = null;
	cardBoundsPx.forEach((bounds) => {
		const startsOnThisPage = bounds.top > sliceTopPx + EPSILON_PX;
		const crossesTheBreak =
			bounds.top < naiveBottomPx - EPSILON_PX && bounds.bottom > naiveBottomPx + EPSILON_PX;
		const fitsWithinOnePage = bounds.bottom - bounds.top <= pagePx + EPSILON_PX;
		if (startsOnThisPage && crossesTheBreak && fitsWithinOnePage) {
			pullTopPx = pullTopPx === null ? bounds.top : Math.min(pullTopPx, bounds.top);
		}
	});
	return pullTopPx;
};
